import { fetchQuery } from 'react-relay';

import { __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__ as platformEnv } from '@atlassian/relay-environment-provider/environment';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import { buildInvitedAppShortcutsRecommendationsVariables } from './PlatformNavigation/AppShortcuts/invitedAppShortcutsRecommendationsVariables.ts';
import InvitedAppShortcutsRecommendationsQuery from './PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

/**
 * SSR preloader for the Invited App Shortcuts GRS recommendations query
 * Mirrors `preloadPlatformNavigationQuery`.
 *
 * Registered in `preloadSideNavigation` / `preloadAccessLinkRoute` alongside
 * `preloadPlatformNavigationQuery`, so it inherits the same route-eligibility.
 * Guarded by BOTH the kill-switch gate AND the experiment
 * cohort so non-cohort users do NOT pay the preload cost.
 *
 * Variables MUST be byte-for-byte identical to the render-time consumer in
 * `InvitedAppShortcutsRecommendations.tsx` (tenantId=cloudId, product=confluence)
 * or the client's store-or-network lookup misses and it refetches. Both sides
 * build them from `buildInvitedAppShortcutsRecommendationsVariables`, which is
 * what actually enforces this.
 */
export const preloadInvitedAppShortcutsRecommendationsQuery = async (): Promise<void> => {
	// Split the kill-switch gate from the experiment so non-cohort users skip the preload.
	// No-exposure evaluation: exposure is logged at render time (ConfluenceGSN), not here.
	const isInvitedAppShortcutsCohort =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate') &&
		UNSAFE_expValEqualsNoExposure('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', true);

	if (!isInvitedAppShortcutsCohort) {
		return;
	}

	const { cloudId, userId } = getPreloaderFnContext();

	// Cohort prerequisites: both are required by the recommendations renderer.
	if (!cloudId || !userId) {
		return;
	}

	// Shared builder — keeps these byte-identical to the render-time consumer.
	const variables = buildInvitedAppShortcutsRecommendationsVariables(cloudId);

	// On the server (Bifrost SSR), await the AGG fetch so the Relay store is populated before
	// render. The client reads the serialised store synchronously via store-or-network — no second
	// network request. Wrapped in `slowQueryGuard` so a slow/hanging GRS call can't stall the
	// side-nav SSR preload: on timeout it resolves and we fall through to the client store-or-network
	// + fail-open default menu. Degrade gracefully if platformEnv lacks auth in the Bifrost context.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		try {
			await slowQueryGuard(
				'preloadInvitedAppShortcutsRecommendationsQuery',
				() =>
					fetchQuery(platformEnv, InvitedAppShortcutsRecommendationsQuery, variables).toPromise(),
				SLOW_QUERY_DEFAULT_TIMEOUT,
			);
		} catch (e) {
			// Degrade gracefully: the client falls through to store-or-network + the fail-open default
			// menu. Log a sanitized message + operation name only — never the raw error object, which
			// could carry backend/tenant detail into SSR logs.
			// eslint-disable-next-line no-console
			console.warn(
				`[InvitedAppShortcutsRecommendations preloader] InvitedAppShortcutsRecommendationsQuery failed in SSR: ${
					e instanceof Error ? e.message : 'unknown error'
				}`,
			);
		}
	}
};
